import { FC, useMemo } from "react";
import { useFormik } from "formik";
import { Flex, FormControl, FormLabel, Input as ChakraInput, Select as ChakraSelect, Button } from "@chakra-ui/react";
import { Recruiter } from "@/model/recruiters";
import { Customer } from "@/model/customers";
import { Project } from "@/model/projects";
import { ApplicationFilters } from "@/model/applications";

type StatusOption = { value: string; label: string };

type FiltersPopoverProps = {
  recruiters: ReadonlyArray<Recruiter>;
  customers: ReadonlyArray<Customer>;
  allProjects: ReadonlyArray<Project>;
  filteredStatusOptions: ReadonlyArray<StatusOption>;
  showStatus: boolean;
  values: ApplicationFilters;
  onApply: (values: ApplicationFilters) => void;
  onReset: () => void;
};

export const FiltersPopover: FC<FiltersPopoverProps> = ({
  recruiters,
  customers,
  allProjects,
  filteredStatusOptions,
  showStatus,
  values,
  onApply,
  onReset,
}) => {
  const formik = useFormik<ApplicationFilters>({
    initialValues: values,
    enableReinitialize: true,
    onSubmit: (submittedValues: ApplicationFilters) => {
      onApply(submittedValues);
    },
  });

  const projects = useMemo(() => {
    if (!formik.values.customer_id) return allProjects as Project[];
    const customerId = formik.values.customer_id;
    return (allProjects as Project[]).filter((p) => String(p.customer?.id) === String(customerId));
  }, [allProjects, formik.values.customer_id]);

  const handleResetLocal = () => {
    formik.resetForm({
      values: {
        customer_id: "",
        project_id: "",
        created_by: "",
        created_at_from: "",
        created_at_to: "",
        ["status[]"]: undefined,
      },
    });
    onReset();
  };

  const handleApplyLocal = () => {
    void formik.submitForm();
  };

  return (
    <>
      <FormControl>
        <FormLabel>Заказчик</FormLabel>
        <ChakraSelect
          placeholder="Все заказчики"
          value={formik.values.customer_id || ""}
          onChange={(e) => {
            const nextValue = e.target.value;
            formik.setFieldValue("customer_id", nextValue);
            formik.setFieldValue("project_id", "");
          }}
        >
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </ChakraSelect>
      </FormControl>

      <FormControl>
        <FormLabel>Проект</FormLabel>
        <ChakraSelect
          placeholder="Все проекты"
          value={formik.values.project_id || ""}
          onChange={(e) => {
            const nextValue = e.target.value;
            formik.setFieldValue("project_id", nextValue);
          }}
        >
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </ChakraSelect>
      </FormControl>

      {showStatus && (
        <FormControl>
          <FormLabel>Статус заявки</FormLabel>
          <ChakraSelect
            placeholder="Все статусы"
            value={Array.isArray(formik.values["status[]"]) && formik.values["status[]"]!.length > 0 ? formik.values["status[]"]![0] : ""}
            onChange={(e) => {
              const nextValue = e.target.value;
              formik.setValues({
                ...formik.values,
                ["status[]"]: nextValue ? [nextValue] : undefined,
              });
            }}
          >
            {filteredStatusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </ChakraSelect>
        </FormControl>
      )}

      <FormControl>
        <FormLabel>Создан кем</FormLabel>
        <ChakraSelect
          placeholder="Все пользователи"
          value={formik.values.created_by || ""}
          onChange={(e) => {
            const nextValue = e.target.value;
            formik.setFieldValue("created_by", nextValue);
          }}
        >
          {recruiters?.map((r) => (
            <option key={r.id} value={r.id}>
              {r.username}
            </option>
          ))}
        </ChakraSelect>
      </FormControl>

      <FormControl>
        <FormLabel>Дата создания</FormLabel>
        <Flex direction="row" gap="12px">
          <ChakraInput
            type="date"
            value={formik.values.created_at_from || ""}
            placeholder="От"
            onChange={(e) => formik.setFieldValue("created_at_from", e.target.value || "")}
          />
          <ChakraInput
            type="date"
            value={formik.values.created_at_to || ""}
            placeholder="До"
            onChange={(e) => formik.setFieldValue("created_at_to", e.target.value || "")}
          />
        </Flex>
      </FormControl>
      <Flex justifyContent="flex-end" gap="8px" mt="12px">
        <Button variant="ghost" onClick={handleResetLocal}>
          Сбросить
        </Button>
        <Button onClick={handleApplyLocal} bg="#2563eb" color="white" _hover={{ bg: "#2563eb" }} _active={{ bg: "#2563eb" }}>
          Применить
        </Button>
      </Flex>
    </>
  );
};
